import { getGameAuth } from '../firebase';
import type { ClaimAudioMetadata } from './amarkoshaFppAudio';
import { getPeerSignal, sendPeerSignal } from './amarkoshaFppRoom';

type PeerStatus = 'connecting' | 'connected' | 'disconnected' | 'failed';
type ControlMessage =
  | { type: 'claim-audio-start'; payload: ClaimAudioMetadata }
  | { type: 'claim-audio-commit'; payload: ClaimAudioMetadata }
  | { type: 'claim-audio-cancel'; claimId: string }
  | { type: 'claim-audio-ack'; claimId: string }
  | { type: 'claim-audio-archive'; payload: ClaimAudioMetadata; chunks: number };

type OutgoingArchive = {
  payload: ClaimAudioMetadata;
  audio: Blob;
  peerIds: Set<string>;
  buffer: Promise<ArrayBuffer>;
  createdAtMs: number;
  attempts: Map<string, number>;
  failures: Map<string, number>;
  expiryTimer: number;
};

type IncomingArchive = {
  payload: ClaimAudioMetadata;
  chunks: number;
  received: ArrayBuffer[];
  bytes: number;
};

const MAX_ARCHIVE_CHUNKS = 256;
const MAX_ARCHIVE_BYTES = 2 * 1024 * 1024;
const MAX_ARCHIVE_ATTEMPTS = 4;
const ARCHIVE_EXPIRY_MS = 2 * 60 * 1000;
const MAX_BUFFERED_ARCHIVE_BYTES = 256 * 1024;

function validClaimMetadata(
  payload: ClaimAudioMetadata,
  remoteId: string,
  roomCode: string,
  committed: boolean,
): boolean {
  return (
    typeof payload?.id === 'string'
    && payload.id.length > 0
    && payload.id.length <= 512
    && typeof payload.matchId === 'string'
    && payload.matchId.startsWith(`${roomCode}-`)
    && payload.matchId.length <= 256
    && typeof payload.roundId === 'string'
    && payload.roundId.length <= 64
    && payload.playerId === remoteId
    && typeof payload.playerName === 'string'
    && payload.playerName.length <= 100
    && typeof payload.word === 'string'
    && payload.word.length <= 100
    && typeof payload.sentence === 'string'
    && payload.sentence.length <= 500
    && typeof payload.transcript === 'string'
    && payload.transcript.length <= 500
    && payload.mimeType === 'audio/wav'
    && Number.isFinite(payload.durationMs)
    && payload.durationMs >= (committed ? 600 : 0)
    && payload.durationMs <= 10_000
    && Number.isFinite(payload.createdAtMs)
  );
}

function sameMetadata(
  left: ClaimAudioMetadata,
  right: ClaimAudioMetadata,
  allowFinalFields: boolean,
): boolean {
  return (
    left.id === right.id
    && left.matchId === right.matchId
    && left.roundId === right.roundId
    && left.playerId === right.playerId
    && left.playerName === right.playerName
    && left.word === right.word
    && left.sentence === right.sentence
    && left.mimeType === right.mimeType
    && left.createdAtMs === right.createdAtMs
    && (
      allowFinalFields
      || (
        left.transcript === right.transcript
        && left.durationMs === right.durationMs
      )
    )
  );
}

interface PeerLink {
  remoteId: string;
  connection: RTCPeerConnection;
  channel: RTCDataChannel | null;
  remoteStream: MediaStream;
  audioSender: RTCRtpSender;
  status: PeerStatus;
  pollId: number | null;
  applyingSignal: boolean;
  sessionId: string;
  remoteNonce: string;
  appliedAnswerSessionId: string;
  offerPublishedSessionId: string;
  pendingLocalCandidates: RTCIceCandidateInit[];
  pendingCandidates: Array<{ sessionId: string; candidate: RTCIceCandidateInit }>;
  seenCandidateIds: Set<string>;
  activeClaimIds: Set<string>;
  incomingClaims: Map<string, ClaimAudioMetadata>;
  committedClaims: Map<string, ClaimAudioMetadata>;
  incomingArchive: IncomingArchive | null;
}

interface PeerMeshOptions {
  roomCode: string;
  playerId: string;
  onRemoteStream: (remoteId: string, stream: MediaStream) => void;
  onClaimStarted: (remoteId: string, claim: ClaimAudioMetadata) => void;
  onClaimCommitted: (remoteId: string, claim: ClaimAudioMetadata) => void;
  onClaimCancelled: (remoteId: string, claimId: string) => void;
  onAudioArchive: (remoteId: string, claim: ClaimAudioMetadata, audio: Blob) => void;
  onAudioSent: (claimId: string, remoteId: string) => void;
  onAudioAcknowledged: (claimId: string, remoteId: string) => void;
  onStatus: (statuses: Record<string, PeerStatus>) => void;
  onError: (message: string) => void;
}

function pairMembers(left: string, right: string): [string, string] {
  const leftPoints = Array.from(left, (character) => character.codePointAt(0) ?? 0);
  const rightPoints = Array.from(right, (character) => character.codePointAt(0) ?? 0);
  const length = Math.min(leftPoints.length, rightPoints.length);
  for (let index = 0; index < length; index += 1) {
    if (leftPoints[index] !== rightPoints[index]) {
      return leftPoints[index] < rightPoints[index] ? [left, right] : [right, left];
    }
  }
  return leftPoints.length < rightPoints.length ? [left, right] : [right, left];
}

async function configuredIceServers(): Promise<RTCIceServer[]> {
  try {
    const token = await getGameAuth().currentUser?.getIdToken();
    if (!token) throw new Error('Missing game authentication.');
    const response = await fetch('/api/fpp/ice', {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (response.ok) {
      const data = await response.json() as { iceServers?: RTCIceServer[] };
      if (Array.isArray(data.iceServers) && data.iceServers.length > 0) {
        return data.iceServers;
      }
    }
  } catch {
    // Direct LAN/STUN connectivity remains available without TURN credentials.
  }
  return [{ urls: 'stun:stun.l.google.com:19302' }];
}

export class FppPeerMesh {
  private readonly links = new Map<string, PeerLink>();

  private readonly pendingLinks = new Set<string>();

  private readonly desiredPeers = new Set<string>();

  private readonly iceServers = configuredIceServers();

  private localAudioTrack: MediaStreamTrack | null = null;

  private activeClaim: ClaimAudioMetadata | null = null;

  private readonly claimParticipants = new Map<string, Set<string>>();

  private readonly pendingControlsByPeer = new Map<string, ControlMessage[]>();

  private readonly unacknowledgedCommits = new Map<string, {
    payload: ClaimAudioMetadata;
    peerIds: Set<string>;
  }>();

  private readonly outgoingArchives = new Map<string, OutgoingArchive>();

  private readonly reconnectTimers = new Map<string, number>();

  private destroyed = false;

  constructor(private readonly options: PeerMeshOptions) {}

  syncPeers(playerIds: string[]): void {
    const wanted = new Set(playerIds.filter((id) => id !== this.options.playerId));
    this.desiredPeers.clear();
    wanted.forEach((id) => this.desiredPeers.add(id));
    wanted.forEach((remoteId) => {
      if (!this.links.has(remoteId) && !this.pendingLinks.has(remoteId)) {
        this.pendingLinks.add(remoteId);
        void this.createLink(remoteId)
          .catch(() => this.options.onError('Unable to create an audio connection.'))
          .finally(() => this.pendingLinks.delete(remoteId));
      }
    });
    this.links.forEach((link, remoteId) => {
      if (!wanted.has(remoteId)) {
        this.removeLink(remoteId, link);
        this.pendingControlsByPeer.delete(remoteId);
        this.unacknowledgedCommits.forEach((pending, claimId) => {
          pending.peerIds.delete(remoteId);
          if (pending.peerIds.size === 0) this.unacknowledgedCommits.delete(claimId);
        });
        this.outgoingArchives.forEach((archive, claimId) => {
          archive.peerIds.delete(remoteId);
          if (archive.peerIds.size === 0) {
            window.clearTimeout(archive.expiryTimer);
            this.outgoingArchives.delete(claimId);
          }
        });
      }
    });
    this.publishStatuses();
  }

  connectedPeerIds(): string[] {
    return [...this.links.values()]
      .filter((link) => link.status === 'connected' && link.channel?.readyState === 'open')
      .map((link) => link.remoteId);
  }

  claimParticipantIds(claimId: string): string[] {
    return [...(this.claimParticipants.get(claimId) ?? new Set<string>())];
  }

  async setLocalAudioTrack(track: MediaStreamTrack | null): Promise<void> {
    this.localAudioTrack = track;
    await Promise.all([...this.links.values()].map(async (link) => {
      try {
        await link.audioSender.replaceTrack(track);
      } catch (error) {
        link.status = 'failed';
        this.publishStatuses();
        this.options.onError('Unable to attach the microphone to an audio connection.');
        throw error;
      }
    }));
  }

  beginAudioClaim(payload: ClaimAudioMetadata): string[] {
    this.activeClaim = payload;
    const participants = new Set<string>();
    this.links.forEach((link) => {
      if (this.sendControl(link, { type: 'claim-audio-start', payload })) {
        link.activeClaimIds.add(payload.id);
        participants.add(link.remoteId);
      }
    });
    this.claimParticipants.set(payload.id, participants);
    return [...participants];
  }

  commitAudioClaim(payload: ClaimAudioMetadata, audio: Blob): string[] {
    if (
      audio.type !== 'audio/wav'
      || audio.size < 256
      || audio.size > MAX_ARCHIVE_BYTES
    ) {
      throw new Error('Accepted audio is not a safe portable WAV archive.');
    }
    this.activeClaim = null;
    const targeted = [...(this.claimParticipants.get(payload.id) ?? new Set<string>())];
    if (targeted.length === 0) {
      this.claimParticipants.delete(payload.id);
      return [];
    }
    this.unacknowledgedCommits.set(payload.id, {
      payload,
      peerIds: new Set(targeted),
    });
    const archive: OutgoingArchive = {
      payload,
      audio,
      peerIds: new Set(targeted),
      buffer: audio.arrayBuffer(),
      createdAtMs: Date.now(),
      attempts: new Map(),
      failures: new Map(),
      expiryTimer: 0,
    };
    this.outgoingArchives.set(payload.id, archive);
    archive.expiryTimer = window.setTimeout(() => {
      if (!this.outgoingArchives.has(payload.id)) return;
      this.outgoingArchives.delete(payload.id);
      this.unacknowledgedCommits.delete(payload.id);
      this.options.onError('A teammate did not acknowledge the accepted audio archive.');
    }, ARCHIVE_EXPIRY_MS);
    targeted.forEach((remoteId) => {
      const link = this.links.get(remoteId);
      const message: ControlMessage = { type: 'claim-audio-commit', payload };
      if (link) {
        this.sendControl(link, message);
        void this.sendArchive(link, archive);
      }
      link?.activeClaimIds.delete(payload.id);
    });
    this.claimParticipants.delete(payload.id);
    return targeted;
  }

  cancelAudioClaim(claimId: string): void {
    if (this.activeClaim?.id === claimId) this.activeClaim = null;
    const participants = this.claimParticipants.get(claimId) ?? new Set<string>();
    participants.forEach((remoteId) => {
      const link = this.links.get(remoteId);
      const message: ControlMessage = { type: 'claim-audio-cancel', claimId };
      if (!link || !this.sendControl(link, message)) this.queueControl(remoteId, message);
      link?.activeClaimIds.delete(claimId);
    });
    this.claimParticipants.delete(claimId);
  }

  acknowledgeAudio(remoteId: string, claimId: string): void {
    const link = this.links.get(remoteId);
    if (link && this.sendControl(link, { type: 'claim-audio-ack', claimId })) {
      link.committedClaims.delete(claimId);
    }
  }

  private markAudioAcknowledged(remoteId: string, claimId: string): void {
    const pending = this.unacknowledgedCommits.get(claimId);
    if (!pending) return;
    pending.peerIds.delete(remoteId);
    if (pending.peerIds.size === 0) this.unacknowledgedCommits.delete(claimId);
    const archive = this.outgoingArchives.get(claimId);
    archive?.peerIds.delete(remoteId);
    if (archive?.peerIds.size === 0) {
      window.clearTimeout(archive.expiryTimer);
      this.outgoingArchives.delete(claimId);
    }
  }

  private async sendArchive(link: PeerLink, archive: OutgoingArchive): Promise<void> {
    const channel = link.channel;
    if (channel?.readyState !== 'open' || !archive.peerIds.has(link.remoteId)) return;
    const attempts = archive.attempts.get(link.remoteId) ?? 0;
    if (
      attempts >= MAX_ARCHIVE_ATTEMPTS
      || Date.now() - archive.createdAtMs > ARCHIVE_EXPIRY_MS
    ) {
      archive.peerIds.delete(link.remoteId);
      const pending = this.unacknowledgedCommits.get(archive.payload.id);
      pending?.peerIds.delete(link.remoteId);
      if (pending?.peerIds.size === 0) {
        this.unacknowledgedCommits.delete(archive.payload.id);
      }
      if (archive.peerIds.size === 0) this.outgoingArchives.delete(archive.payload.id);
      if (archive.peerIds.size === 0) window.clearTimeout(archive.expiryTimer);
      this.options.onError('A teammate did not acknowledge the accepted audio archive.');
      return;
    }
    const buffer = await archive.buffer;
    if (channel.readyState !== 'open' || !archive.peerIds.has(link.remoteId)) return;
    const chunkSize = 12 * 1024;
    const chunks = Math.max(1, Math.ceil(buffer.byteLength / chunkSize));
    try {
      channel.send(JSON.stringify({
        type: 'claim-audio-archive',
        payload: archive.payload,
        chunks,
      } satisfies ControlMessage));
      for (let offset = 0; offset < buffer.byteLength; offset += chunkSize) {
        while (channel.bufferedAmount > MAX_BUFFERED_ARCHIVE_BYTES) {
          if (channel.readyState !== 'open') throw new Error('Audio data channel closed.');
          await new Promise((resolve) => window.setTimeout(resolve, 20));
        }
        if (channel.readyState !== 'open') throw new Error('Audio data channel closed.');
        channel.send(buffer.slice(offset, Math.min(buffer.byteLength, offset + chunkSize)));
      }
      if (buffer.byteLength === 0) channel.send(new ArrayBuffer(0));
      archive.attempts.set(link.remoteId, attempts + 1);
      archive.failures.set(link.remoteId, 0);
    } catch {
      const failures = (archive.failures.get(link.remoteId) ?? 0) + 1;
      archive.failures.set(link.remoteId, failures);
      if (failures >= MAX_ARCHIVE_ATTEMPTS) {
        archive.peerIds.delete(link.remoteId);
        this.options.onError('A teammate could not receive the accepted audio archive.');
        return;
      }
      link.status = 'failed';
      this.publishStatuses();
      this.scheduleReconnect(link.remoteId, link, 250);
    }
  }

  destroy(): void {
    this.destroyed = true;
    this.reconnectTimers.forEach((timerId) => window.clearTimeout(timerId));
    this.reconnectTimers.clear();
    this.outgoingArchives.forEach((archive) => window.clearTimeout(archive.expiryTimer));
    this.outgoingArchives.clear();
    this.links.forEach((link, remoteId) => this.removeLink(remoteId, link));
  }

  private queueControl(remoteId: string, message: ControlMessage): void {
    const pending = this.pendingControlsByPeer.get(remoteId) ?? [];
    const claimId = message.type === 'claim-audio-commit'
      ? message.payload.id
      : message.type === 'claim-audio-cancel'
        ? message.claimId
        : '';
    if (pending.some((item) => {
      if (item.type !== message.type) return false;
      if (item.type === 'claim-audio-commit') return item.payload.id === claimId;
      if (item.type === 'claim-audio-cancel') return item.claimId === claimId;
      return false;
    })) return;
    pending.push(message);
    this.pendingControlsByPeer.set(remoteId, pending);
  }

  private flushQueuedControls(link: PeerLink): void {
    const pending = this.pendingControlsByPeer.get(link.remoteId) ?? [];
    const remaining = pending.filter((message) => !this.sendControl(link, message));
    if (remaining.length > 0) this.pendingControlsByPeer.set(link.remoteId, remaining);
    else this.pendingControlsByPeer.delete(link.remoteId);
  }

  private scheduleReconnect(remoteId: string, link: PeerLink, delayMs: number): void {
    if (
      this.destroyed
      || !this.desiredPeers.has(remoteId)
      || this.links.get(remoteId) !== link
      || this.reconnectTimers.has(remoteId)
    ) return;
    const timerId = window.setTimeout(() => {
      this.reconnectTimers.delete(remoteId);
      if (this.links.get(remoteId) === link) this.removeLink(remoteId, link);
      this.syncPeers([...this.desiredPeers, this.options.playerId]);
    }, delayMs);
    this.reconnectTimers.set(remoteId, timerId);
  }

  private sendControl(link: PeerLink, message: ControlMessage): boolean {
    const channel = link.channel;
    if (channel?.readyState !== 'open') return false;
    try {
      channel.send(JSON.stringify(message));
      if (message.type === 'claim-audio-commit') {
        this.options.onAudioSent(message.payload.id, link.remoteId);
      }
      return true;
    } catch {
      link.status = 'failed';
      this.publishStatuses();
      return false;
    }
  }

  private async createLink(remoteId: string): Promise<void> {
    const [a] = pairMembers(this.options.playerId, remoteId);
    const initiator = this.options.playerId === a;
    const iceServers = await this.iceServers;
    if (this.destroyed || this.links.has(remoteId)) return;

    const connection = new RTCPeerConnection({ iceServers, iceCandidatePoolSize: 4 });
    const audioTransceiver = connection.addTransceiver('audio', { direction: 'sendrecv' });
    if (this.localAudioTrack) await audioTransceiver.sender.replaceTrack(this.localAudioTrack);
    const link: PeerLink = {
      remoteId,
      connection,
      channel: null,
      remoteStream: new MediaStream(),
      audioSender: audioTransceiver.sender,
      status: 'connecting',
      pollId: null,
      applyingSignal: false,
      sessionId: '',
      remoteNonce: '',
      appliedAnswerSessionId: '',
      offerPublishedSessionId: '',
      pendingLocalCandidates: [],
      pendingCandidates: [],
      seenCandidateIds: new Set(),
      activeClaimIds: new Set(),
      incomingClaims: new Map(),
      committedClaims: new Map(),
      incomingArchive: null,
    };
    this.links.set(remoteId, link);
    this.publishStatuses();

    connection.ontrack = (event) => {
      const tracks = event.streams[0]?.getTracks() ?? [event.track];
      tracks.forEach((track) => {
        if (!link.remoteStream.getTracks().some((existing) => existing.id === track.id)) {
          link.remoteStream.addTrack(track);
        }
      });
      this.options.onRemoteStream(remoteId, link.remoteStream);
    };
    connection.onconnectionstatechange = () => {
      const state = connection.connectionState;
      link.status = state === 'connected'
        ? (link.channel?.readyState === 'open' ? 'connected' : 'connecting')
        : state === 'failed'
          ? 'failed'
          : state === 'closed' || state === 'disconnected'
            ? 'disconnected'
            : 'connecting';
      this.publishStatuses();
      if (state === 'failed') this.scheduleReconnect(remoteId, link, 250);
      else if (state === 'disconnected') this.scheduleReconnect(remoteId, link, 3_000);
    };
    connection.onicecandidate = (event) => {
      if (!event.candidate || !link.sessionId) return;
      const candidate = event.candidate.toJSON();
      if (initiator && link.offerPublishedSessionId !== link.sessionId) {
        link.pendingLocalCandidates.push(candidate);
        return;
      }
      void sendPeerSignal(this.options.roomCode, remoteId, {
        kind: 'candidate',
        sessionId: link.sessionId,
        candidate,
      }).catch(() => this.options.onError('Unable to exchange audio connection details.'));
    };

    if (initiator) {
      this.attachChannel(link, connection.createDataChannel('amarkosha-control', { ordered: true }));
    } else {
      connection.ondatachannel = (event) => this.attachChannel(link, event.channel);
    }

    const publishOffer = async (iceRestart: boolean) => {
      if (!iceRestart && link.sessionId && connection.localDescription) return;
      link.sessionId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      const offer = await connection.createOffer({ iceRestart });
      await connection.setLocalDescription(offer);
      await sendPeerSignal(this.options.roomCode, remoteId, {
        kind: 'offer',
        sessionId: link.sessionId,
        description: { type: offer.type, sdp: offer.sdp },
      });
      link.offerPublishedSessionId = link.sessionId;
      const pending = link.pendingLocalCandidates.splice(0);
      await Promise.all(pending.map((candidate) => sendPeerSignal(this.options.roomCode, remoteId, {
        kind: 'candidate',
        sessionId: link.sessionId,
        candidate,
      })));
    };

    const pollSignal = async () => {
      if (this.destroyed || !this.links.has(remoteId) || link.status === 'connected') return;
      const { signal, candidates } = await getPeerSignal(this.options.roomCode, remoteId);
      if (!signal || link.applyingSignal) return;
      if (initiator && signal.bNonce && signal.bNonce !== link.remoteNonce) {
        const iceRestart = !!link.remoteNonce;
        link.remoteNonce = signal.bNonce;
        link.applyingSignal = true;
        void publishOffer(iceRestart)
          .catch(() => this.options.onError('Unable to create an audio connection.'))
          .finally(() => { link.applyingSignal = false; });
        return;
      }
      if (!initiator && signal.offer && signal.sessionId && signal.sessionId !== link.sessionId) {
        const offer = signal.offer;
        link.sessionId = signal.sessionId;
        link.applyingSignal = true;
        void (async () => {
          await connection.setRemoteDescription(offer);
          await this.flushCandidates(link);
          const answer = await connection.createAnswer();
          await connection.setLocalDescription(answer);
          await sendPeerSignal(this.options.roomCode, remoteId, {
            kind: 'answer',
            sessionId: link.sessionId,
            description: { type: answer.type, sdp: answer.sdp },
          });
        })()
          .catch(() => this.options.onError('Unable to answer an audio connection.'))
          .finally(() => { link.applyingSignal = false; });
      } else if (
        initiator
        && signal.answer
        && signal.answerSessionId === link.sessionId
        && signal.answerSessionId !== link.appliedAnswerSessionId
      ) {
        link.applyingSignal = true;
        void connection.setRemoteDescription(signal.answer)
          .then(() => {
            link.appliedAnswerSessionId = signal.answerSessionId ?? '';
            return this.flushCandidates(link);
          })
          .catch(() => this.options.onError('Unable to complete an audio connection.'))
          .finally(() => { link.applyingSignal = false; });
      }
      candidates.forEach((candidate) => {
        if (link.seenCandidateIds.has(candidate.id)) return;
        link.seenCandidateIds.add(candidate.id);
        if (candidate.from === this.options.playerId || !candidate.candidate || !candidate.sessionId) return;
        if (candidate.sessionId !== link.sessionId || !connection.remoteDescription) {
          link.pendingCandidates.push({
            sessionId: candidate.sessionId,
            candidate: candidate.candidate,
          });
          return;
        }
        void connection.addIceCandidate(candidate.candidate)
          .catch(() => this.options.onError('Unable to add an audio connection candidate.'));
      });
    };

    const poll = () => {
      void pollSignal().catch(() => this.options.onError('Audio signaling is unavailable.'));
    };
    poll();
    link.pollId = window.setInterval(poll, 500);

    if (!initiator) {
      try {
        await sendPeerSignal(this.options.roomCode, remoteId, {
          kind: 'nonce',
          nonce: `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
        });
      } catch {
        link.status = 'failed';
        this.publishStatuses();
        this.options.onError('Unable to start an audio connection.');
      }
    }
  }

  private attachChannel(link: PeerLink, channel: RTCDataChannel): void {
    link.channel = channel;
    channel.onopen = () => {
      link.status = 'connected';
      this.publishStatuses();
      if (this.activeClaim && this.sendControl(link, { type: 'claim-audio-start', payload: this.activeClaim })) {
        link.activeClaimIds.add(this.activeClaim.id);
        const participants = this.claimParticipants.get(this.activeClaim.id) ?? new Set<string>();
        participants.add(link.remoteId);
        this.claimParticipants.set(this.activeClaim.id, participants);
      }
      this.flushQueuedControls(link);
      this.unacknowledgedCommits.forEach(({ payload, peerIds }) => {
        if (peerIds.has(link.remoteId)) {
          this.sendControl(link, { type: 'claim-audio-commit', payload });
        }
      });
      this.outgoingArchives.forEach((archive) => {
        if (archive.peerIds.has(link.remoteId)) void this.sendArchive(link, archive);
      });
    };
    channel.onclose = () => {
      link.status = 'disconnected';
      this.publishStatuses();
      this.scheduleReconnect(link.remoteId, link, 500);
    };
    channel.onerror = () => {
      link.status = 'failed';
      this.publishStatuses();
      this.scheduleReconnect(link.remoteId, link, 250);
    };
    channel.binaryType = 'arraybuffer';
    channel.onmessage = (event) => {
      if (event.data instanceof ArrayBuffer) {
        const archive = link.incomingArchive;
        if (!archive) return;
        archive.bytes += event.data.byteLength;
        if (archive.bytes > MAX_ARCHIVE_BYTES) {
          link.incomingArchive = null;
          this.options.onError('A received audio archive exceeded the safe size limit.');
          return;
        }
        archive.received.push(event.data);
        if (archive.received.length === archive.chunks) {
          link.incomingArchive = null;
          this.options.onAudioArchive(
            link.remoteId,
            archive.payload,
            new Blob(archive.received, { type: archive.payload.mimeType || 'audio/wav' }),
          );
        }
        return;
      }
      if (typeof event.data !== 'string') return;
      try {
        const message = JSON.parse(event.data) as ControlMessage;
        if (message.type === 'claim-audio-start') {
          if (!validClaimMetadata(message.payload, link.remoteId, this.options.roomCode, false)) {
            this.options.onError('A received audio claim was invalid.');
            return;
          }
          link.incomingClaims.set(message.payload.id, message.payload);
          this.options.onClaimStarted(link.remoteId, message.payload);
        } else if (message.type === 'claim-audio-commit') {
          const started = link.incomingClaims.get(message.payload.id);
          if (
            !started
            || !validClaimMetadata(message.payload, link.remoteId, this.options.roomCode, true)
            || !sameMetadata(started, message.payload, true)
          ) {
            this.options.onError('A received audio commit was invalid.');
            return;
          }
          link.incomingClaims.delete(message.payload.id);
          link.committedClaims.set(message.payload.id, message.payload);
          this.options.onClaimCommitted(link.remoteId, message.payload);
        } else if (message.type === 'claim-audio-cancel') {
          if (link.incomingArchive?.payload.id === message.claimId) link.incomingArchive = null;
          link.incomingClaims.delete(message.claimId);
          link.committedClaims.delete(message.claimId);
          this.options.onClaimCancelled(link.remoteId, message.claimId);
        } else if (message.type === 'claim-audio-ack') {
          this.markAudioAcknowledged(link.remoteId, message.claimId);
          this.options.onAudioAcknowledged(message.claimId, link.remoteId);
        } else if (message.type === 'claim-audio-archive') {
          const committed = link.committedClaims.get(message.payload.id);
          if (
            !committed
            || !validClaimMetadata(message.payload, link.remoteId, this.options.roomCode, true)
            || !sameMetadata(committed, message.payload, false)
            || !Number.isInteger(message.chunks)
            || message.chunks < 1
            || message.chunks > MAX_ARCHIVE_CHUNKS
          ) {
            this.options.onError('A received audio archive was invalid.');
            return;
          }
          link.incomingArchive = {
            payload: committed,
            chunks: message.chunks,
            received: [],
            bytes: 0,
          };
        }
      } catch {
        this.options.onError('A received audio control message was invalid.');
      }
    };
  }

  private async flushCandidates(link: PeerLink): Promise<void> {
    const candidates = link.pendingCandidates.splice(0);
    for (const pending of candidates) {
      if (pending.sessionId === link.sessionId) {
        await link.connection.addIceCandidate(pending.candidate);
      }
    }
  }

  private removeLink(remoteId: string, link: PeerLink): void {
    if (link.pollId !== null) window.clearInterval(link.pollId);
    if (this.links.get(remoteId) === link) this.links.delete(remoteId);
    if (link.channel) link.channel.onclose = null;
    link.connection.onconnectionstatechange = null;
    link.channel?.close();
    link.connection.close();
    this.publishStatuses();
  }

  private publishStatuses(): void {
    if (this.destroyed) return;
    const statuses: Record<string, PeerStatus> = {};
    this.links.forEach((link, id) => { statuses[id] = link.status; });
    this.options.onStatus(statuses);
  }
}
